import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArrowRight,
  CircleDashed,
  Crown,
  DownloadCloud,
  Eye,
  FileText,
  FolderKanban,
  FolderOpen,
  Hash,
  ListTodo,
  Plus,
  Search,
  Sparkles,
  Tag,
  User,
  UsersRound,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';

import { JiraImportDialog } from '@/components/domain/jira-import-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { extractApiErrorMessage } from '@/lib/api';
import { createProject, listProjects, type ProjectSummary } from '@/lib/project-api';
import { useAuthStore } from '@/stores/auth-store';
import { cn } from '@/lib/utils';

const ROLE_META: Record<
  'LEAD' | 'MEMBER' | 'VIEWER',
  { label: string; icon: typeof Crown; pill: string }
> = {
  LEAD: {
    label: 'Lead',
    icon: Crown,
    pill: 'bg-amber-500/10 text-amber-700 ring-amber-500/30 dark:text-amber-300',
  },
  MEMBER: {
    label: 'Member',
    icon: User,
    pill: 'bg-sky-500/10 text-sky-700 ring-sky-500/30 dark:text-sky-300',
  },
  VIEWER: {
    label: 'Viewer',
    icon: Eye,
    pill: 'bg-slate-500/10 text-slate-700 ring-slate-500/30 dark:text-slate-300',
  },
};

type ArchiveFilter = 'ACTIVE' | 'ARCHIVED' | 'ALL';

const ARCHIVE_FILTERS: Array<{ id: ArchiveFilter; label: string; icon: typeof FolderOpen }> = [
  { id: 'ACTIVE', label: 'Active', icon: FolderOpen },
  { id: 'ARCHIVED', label: 'Archived', icon: Archive },
  { id: 'ALL', label: 'All', icon: FolderKanban },
];

function suggestKey(name: string): string {
  const alphaOnly = name
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 5);
  return alphaOnly.length >= 2 ? alphaOnly : alphaOnly.padEnd(2, 'X');
}

// Pick a stable accent color per project (seeded off the key) so the grid looks
// varied without every card requiring its own config.
const CARD_ACCENTS = [
  { strip: 'from-emerald-500 via-emerald-400/60', chipBg: 'bg-emerald-500/10 text-emerald-600 ring-emerald-500/20 dark:text-emerald-400' },
  { strip: 'from-sky-500 via-sky-400/60', chipBg: 'bg-sky-500/10 text-sky-600 ring-sky-500/20 dark:text-sky-400' },
  { strip: 'from-violet-500 via-violet-400/60', chipBg: 'bg-violet-500/10 text-violet-600 ring-violet-500/20 dark:text-violet-400' },
  { strip: 'from-rose-500 via-rose-400/60', chipBg: 'bg-rose-500/10 text-rose-600 ring-rose-500/20 dark:text-rose-400' },
  { strip: 'from-amber-500 via-amber-400/60', chipBg: 'bg-amber-500/10 text-amber-600 ring-amber-500/20 dark:text-amber-400' },
  { strip: 'from-teal-500 via-teal-400/60', chipBg: 'bg-teal-500/10 text-teal-600 ring-teal-500/20 dark:text-teal-400' },
];
function accentFor(key: string) {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) | 0;
  return CARD_ACCENTS[Math.abs(hash) % CARD_ACCENTS.length]!;
}

export function OrgProjectsPage() {
  const { orgSlug } = useParams();
  const activeOrgId = useAuthStore((s) => s.activeOrgId);
  const user = useAuthStore((s) => s.user);
  const membership = user?.memberships.find((m) => m.organizationId === activeOrgId);
  const canCreate = Boolean(
    user?.isSuperAdmin || membership?.permissions.includes('project.create'),
  );

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [archived, setArchived] = useState<ArchiveFilter>('ACTIVE');
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const query = useQuery({
    queryKey: ['org', activeOrgId, 'projects', { debounced, archived }],
    queryFn: () =>
      listProjects(activeOrgId!, {
        ...(debounced ? { search: debounced } : {}),
        includeArchived: archived !== 'ACTIVE',
      }),
    enabled: Boolean(activeOrgId),
  });

  const allItems = query.data?.items ?? [];
  const projects = useMemo(() => {
    if (archived === 'ARCHIVED') return allItems.filter((p) => p.archivedAt);
    return allItems;
  }, [allItems, archived]);

  const empty = !query.isLoading && projects.length === 0;

  // Top-level stats (computed from the "all" response so the hero numbers don't
  // jump every time the archive filter is toggled).
  const totalActive = allItems.filter((p) => !p.archivedAt).length;
  const totalArchived = allItems.filter((p) => p.archivedAt).length;
  const totalOpenIssues = allItems.reduce((sum, p) => sum + p.openIssueCount, 0);

  const filtersActive = Boolean(debounced) || archived !== 'ACTIVE';

  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 p-6">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-xl border bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
        <div
          className="absolute -right-10 -top-10 h-44 w-44 rounded-full bg-primary/10 blur-3xl"
          aria-hidden
        />
        <div className="relative flex flex-wrap items-start justify-between gap-4 p-6">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
              <FolderKanban className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold tracking-tight">Projects</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Every project you have access to in this organization.
              </p>
            </div>
          </div>
          {canCreate ? (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => setImportOpen(true)}
                className="gap-1.5"
                title="Import from Jira"
              >
                <DownloadCloud className="h-4 w-4" aria-hidden /> Import from Jira
              </Button>
              <Button onClick={() => setCreateOpen(true)} className="gap-1.5">
                <Plus className="h-4 w-4" aria-hidden /> New project
              </Button>
            </div>
          ) : null}
        </div>
        <div className="grid grid-cols-3 gap-px border-t bg-border">
          <HeroStat icon={FolderOpen} label="Active" value={String(totalActive)} tone="primary" />
          <HeroStat
            icon={Archive}
            label="Archived"
            value={String(totalArchived)}
            tone={totalArchived > 0 ? 'amber' : 'muted'}
          />
          <HeroStat icon={ListTodo} label="Open issues" value={String(totalOpenIssues)} tone="primary" />
        </div>
      </div>

      {/* Filters */}
      <Card className="overflow-hidden">
        <CardContent className="space-y-3 p-4">
          <div className="grid gap-3 md:grid-cols-[1fr_auto]">
            <div>
              <Label
                htmlFor="proj-search"
                className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
              >
                <Search className="h-3 w-3" aria-hidden /> Search
              </Label>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  id="proj-search"
                  className="h-10 pl-9 pr-9 text-sm"
                  placeholder="Search by name or key…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search ? (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden />
                  </button>
                ) : null}
              </div>
            </div>

            <div>
              <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <Archive className="h-3 w-3" aria-hidden /> Show
              </div>
              <div className="inline-flex items-center gap-1 rounded-lg border bg-muted/30 p-1">
                {ARCHIVE_FILTERS.map((f) => {
                  const Icon = f.icon;
                  const selected = archived === f.id;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setArchived(f.id)}
                      aria-pressed={selected}
                      className={cn(
                        'inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-medium transition-all',
                        selected
                          ? 'bg-background text-foreground shadow-sm ring-1 ring-border'
                          : 'text-muted-foreground hover:bg-background/60 hover:text-foreground',
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" aria-hidden />
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {filtersActive ? (
            <div className="flex items-center justify-between border-t pt-3 text-xs text-muted-foreground">
              <span>Filters active</span>
              <button
                type="button"
                onClick={() => {
                  setSearch('');
                  setArchived('ACTIVE');
                }}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 font-medium text-foreground transition-colors hover:bg-muted"
              >
                <X className="h-3 w-3" aria-hidden /> Clear all
              </button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {query.isError ? (
        <div className="rounded-xl border border-dashed bg-muted/20 p-12">
          <div className="mx-auto flex max-w-md flex-col items-center gap-3 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-500/10 text-rose-600 ring-1 ring-rose-500/20 dark:text-rose-400">
              <FolderKanban className="h-6 w-6" aria-hidden />
            </div>
            <div>
              <h3 className="text-base font-semibold">Could not load projects</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {extractApiErrorMessage(query.error)}
              </p>
            </div>
            <Button onClick={() => query.refetch()} className="gap-1.5">
              Retry
            </Button>
          </div>
        </div>
      ) : empty ? (
        <div className="rounded-xl border border-dashed bg-muted/20 p-12">
          <div className="mx-auto flex max-w-md flex-col items-center gap-3 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/20">
              <FolderKanban className="h-6 w-6" aria-hidden />
            </div>
            <div>
              <h3 className="text-base font-semibold">
                {debounced ? 'No matching projects' : 'No projects yet'}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                {debounced
                  ? 'Clear the search to see every project you can access.'
                  : canCreate
                    ? 'Create your first project to start tracking work.'
                    : 'Ask an org admin to add you to a project.'}
              </p>
            </div>
            {!debounced && canCreate ? (
              <Button onClick={() => setCreateOpen(true)} className="gap-1.5">
                <Plus className="h-4 w-4" aria-hidden /> New project
              </Button>
            ) : filtersActive ? (
              <Button
                variant="outline"
                onClick={() => {
                  setSearch('');
                  setArchived('ACTIVE');
                }}
                className="gap-1.5"
              >
                <X className="h-4 w-4" aria-hidden /> Clear filters
              </Button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {query.isLoading
            ? Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-44 w-full" />)
            : projects.map((project) => (
                <ProjectCard key={project.id} project={project} orgSlug={orgSlug ?? ''} />
              ))}
          {canCreate && projects.length > 0 && !query.isLoading ? (
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="group flex min-h-[11rem] flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border bg-muted/10 p-6 text-muted-foreground transition-all hover:-translate-y-0.5 hover:border-primary/50 hover:bg-primary/5 hover:text-primary hover:shadow-md"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-background ring-1 ring-border transition-colors group-hover:bg-primary/10 group-hover:text-primary group-hover:ring-primary/30">
                <Plus className="h-5 w-5" aria-hidden />
              </div>
              <span className="text-sm font-semibold">New project</span>
            </button>
          ) : null}
        </div>
      )}

      <CreateProjectDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        orgId={activeOrgId ?? ''}
        orgSlug={orgSlug ?? ''}
        onCreated={() => query.refetch()}
      />

      <JiraImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        orgId={activeOrgId ?? ''}
        onImported={() => {
          query.refetch();
        }}
      />
    </div>
  );
}

function HeroStat({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof FolderOpen;
  label: string;
  value: string;
  tone?: 'primary' | 'amber' | 'muted';
}) {
  const toneCls =
    tone === 'amber'
      ? 'bg-amber-500/10 text-amber-600 ring-amber-500/30 dark:text-amber-400'
      : tone === 'muted'
        ? 'bg-muted text-muted-foreground ring-border'
        : 'bg-primary/5 text-primary ring-primary/20';
  return (
    <div className="flex items-center gap-3 bg-background px-4 py-3">
      <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl ring-1', toneCls)}>
        <Icon className="h-5 w-5" aria-hidden />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div className="truncate text-xl font-semibold tabular-nums">{value}</div>
      </div>
    </div>
  );
}

function ProjectCard({ project, orgSlug }: { project: ProjectSummary; orgSlug: string }) {
  const accent = accentFor(project.key);
  const archived = Boolean(project.archivedAt);
  const initials = project.name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s.charAt(0).toUpperCase())
    .join('');
  const roleMeta = project.role ? ROLE_META[project.role] : null;
  const RoleIcon = roleMeta?.icon;

  return (
    <Link
      to={`/o/${orgSlug}/projects/${project.key}`}
      className={cn(
        'group relative flex min-h-[11rem] flex-col overflow-hidden rounded-xl border bg-card shadow-sm transition-all',
        'hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md',
        archived ? 'opacity-75' : '',
      )}
    >
      <div className={cn('h-1 w-full bg-gradient-to-r to-transparent', accent.strip)} aria-hidden />
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-start gap-3">
          <div
            className={cn(
              'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-sm font-bold ring-1',
              accent.chipBg,
            )}
          >
            {initials || <FolderKanban className="h-5 w-5" aria-hidden />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-muted-foreground ring-1 ring-border">
                {project.key}
              </span>
              {archived ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-700 ring-1 ring-amber-500/30 dark:text-amber-300">
                  <Archive className="h-3 w-3" aria-hidden /> Archived
                </span>
              ) : null}
            </div>
            <div className="mt-1 flex items-center gap-1 text-base font-semibold text-foreground transition-colors group-hover:text-primary">
              <span className="truncate">{project.name}</span>
              <ArrowRight
                className="h-3.5 w-3.5 shrink-0 translate-x-0 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100"
                aria-hidden
              />
            </div>
          </div>
        </div>

        {project.description ? (
          <p className="line-clamp-2 text-sm text-muted-foreground">{project.description}</p>
        ) : (
          <p className="text-sm italic text-muted-foreground">No description yet.</p>
        )}

        <div className="mt-auto flex flex-wrap items-center gap-2 text-xs">
          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 tabular-nums text-muted-foreground ring-1 ring-border">
            <UsersRound className="h-3 w-3" aria-hidden />
            {project.memberCount}
          </span>
          <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 tabular-nums text-muted-foreground ring-1 ring-border">
            <ListTodo className="h-3 w-3" aria-hidden />
            {project.openIssueCount} open
          </span>
          {roleMeta && RoleIcon ? (
            <span
              className={cn(
                'ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1',
                roleMeta.pill,
              )}
            >
              <RoleIcon className="h-3 w-3" aria-hidden />
              {roleMeta.label}
            </span>
          ) : null}
        </div>
      </div>
    </Link>
  );
}

function CreateProjectDialog({
  open,
  onOpenChange,
  orgId,
  orgSlug,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  orgId: string;
  orgSlug: string;
  onCreated: () => void;
}) {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [keyDirty, setKeyDirty] = useState(false);
  const [description, setDescription] = useState('');

  const computedKey = keyDirty ? key : suggestKey(name);

  useEffect(() => {
    if (!open) {
      setName('');
      setKey('');
      setKeyDirty(false);
      setDescription('');
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () =>
      createProject(orgId, {
        name: name.trim(),
        key: computedKey,
        ...(description.trim() ? { description: description.trim() } : {}),
      }),
    onSuccess: (project) => {
      success('Project created', `${project.key} is ready for issues`);
      qc.invalidateQueries({ queryKey: ['org', orgId, 'projects'] });
      onCreated();
      onOpenChange(false);
      window.history.pushState(null, '', `/o/${orgSlug}/projects/${project.key}`);
    },
    onError: (e) => error('Could not create project', extractApiErrorMessage(e)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (mutation.isPending) return;
    mutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl overflow-hidden p-0">
        {/* Gradient header */}
        <div className="relative border-b bg-gradient-to-br from-primary/10 via-primary/5 to-transparent px-6 pb-5 pt-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
              <FolderKanban className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <DialogHeader>
                <DialogTitle className="text-xl">Create project</DialogTitle>
                <DialogDescription className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                  <span>Issues will get keys like</span>
                  <span className="inline-flex items-center gap-1 rounded-md bg-background/80 px-2 py-0.5 font-mono text-[11px] font-medium ring-1 ring-border">
                    {computedKey || 'AB'}-1
                  </span>
                </DialogDescription>
              </DialogHeader>
            </div>
          </div>
        </div>

        <form className="space-y-5 px-6 pb-6 pt-5" onSubmit={submit} noValidate>
          <div className="grid gap-4 sm:grid-cols-[1fr_160px]">
            <div className="space-y-2">
              <Label
                htmlFor="proj-name"
                className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              >
                <Tag className="h-3.5 w-3.5" aria-hidden /> Project name
              </Label>
              <Input
                id="proj-name"
                required
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Growth Initiatives"
                className="h-11 text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="proj-key"
                className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
              >
                <Hash className="h-3.5 w-3.5" aria-hidden /> Key
              </Label>
              <Input
                id="proj-key"
                required
                maxLength={10}
                pattern="^[A-Z][A-Z0-9]{1,9}$"
                value={computedKey}
                onChange={(e) => {
                  setKey(e.target.value.toUpperCase());
                  setKeyDirty(true);
                }}
                className="h-11 font-mono text-sm uppercase"
              />
            </div>
          </div>
          <p className="-mt-3 text-[11px] text-muted-foreground">
            2–10 uppercase letters or digits, starting with a letter. Immutable after creation.
          </p>

          <div className="space-y-2">
            <Label
              htmlFor="proj-desc"
              className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
            >
              <FileText className="h-3.5 w-3.5" aria-hidden /> Description
              <span className="ml-1 font-normal normal-case text-muted-foreground/80">
                (optional)
              </span>
            </Label>
            <div className="relative rounded-lg border focus-within:ring-2 focus-within:ring-ring">
              <textarea
                id="proj-desc"
                className="min-h-[100px] w-full resize-y rounded-lg bg-background p-3 pr-16 text-sm focus:outline-none"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={1000}
                placeholder="What is this project for?"
              />
              <span className="pointer-events-none absolute bottom-2 right-3 text-[10px] tabular-nums text-muted-foreground">
                {description.length}/1000
              </span>
            </div>
          </div>

          <DialogFooter className="pt-1">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending || !name.trim() || !computedKey}
              className="gap-1.5"
            >
              {mutation.isPending ? (
                <>
                  <CircleDashed className="h-4 w-4 animate-spin" aria-hidden /> Creating…
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" aria-hidden /> Create project
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
